import * as React from 'react';

export interface UserPreference {
  type?: string;
  component?: string;
  name: string;
  value: string;
}

/**
 * Limiting interface to known use cases only
 */
export interface UserPreferencesReturn {
  userPreferences: UserPreference[];
  getUserPreferenceValueByName: (name: string, defaultValue?: any) => string | undefined;
  updateUserPreferenceValueByName: (name: string, value: string) => void;
}

export const UserPreferencesContext = React.createContext<UserPreferencesReturn>(
  {} as UserPreferencesReturn
);

interface UserPreferencesProps {
  userPreferences: UserPreference | UserPreference[] | null | undefined;
}

const parseUserPreferencesProp = (userPreferences: UserPreferencesProps['userPreferences']) => {
  if (!userPreferences) return [];
  if (Array.isArray(userPreferences)) return userPreferences;
  return [userPreferences];
};

/**
 * Typical usage within gui_defs view_react_field:
 * $field = new view_react_field();
 * $field->react_root = "FooBar";
 * $field->react_props = [
 *     "userPreferences" => [[
 *         "type" => $this->type,
 *         "component" => $this->name,
 *         "name" => $pref_name = "foo",
 *         "value" => $this->getPref($pref_name, "bar"),
 *     ]],
 * ];
 * $this->addField($tab, $col, $field);
 */
export const UserPreferencesProvider: React.FC<UserPreferencesProps> = ({
  userPreferences: _userPreferences,
  children,
}) => {
  const [userPreferences, setUserPreferences] = React.useState<UserPreference[]>(
    parseUserPreferencesProp(_userPreferences)
  );

  /**
   * User preference values are stored in the `gui_pref` table as string
   */
  const getUserPreferenceValueByName = (name: string, defaultValue?: any) => {
    const userPreference = userPreferences?.find((userPreference) => {
      return userPreference.name === name;
    });
    return userPreference?.value ?? defaultValue;
  };

  /**
   * User preference values are stored in the `gui_pref` table as string
   * type and component must be present in order to persist the user preference in the `gui_pref` table
   */
  const updateUserPreferenceValueByName = (name: string, value: string) => {
    let updatedPreference: UserPreference | undefined;
    const updatedUserPreferences = userPreferences.map((userPreference) => {
      if (userPreference.name === name) {
        updatedPreference = { ...userPreference, value };
        return updatedPreference;
      }
      return userPreference;
    });
    setUserPreferences(updatedUserPreferences);

    if (updatedPreference) {
      if (!window.lsCloud?.UserPreferences?.save) return;
      if (!updatedPreference.type || !updatedPreference.component) return;
      window.lsCloud.UserPreferences.save(
        updatedPreference.type,
        updatedPreference.component,
        updatedPreference.name,
        updatedPreference.value
      );
    }
  };

  return (
    <UserPreferencesContext.Provider
      value={{
        userPreferences,
        getUserPreferenceValueByName,
        updateUserPreferenceValueByName,
      }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};
